import { useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePermission } from '@/auth';
import { useAuthStore } from '@/auth/auth-store';
import { useRolesList } from '@/settings/services';
import { RoleEditDialog } from '@/settings/components/role-edit-dialog';
import type { RolesListItem } from '@/types/settings-api';

/**
 * Onboarding wizard tab 2 — Review Roles (PROD-870, epic PROD-856).
 *
 * Flutter parity: `practice_setup_module/practice_setup/presentation/widgets/
 * roles/review_roles_widget.dart`. Lists the standard roles (pre-seeded
 * server-side on org creation) and lets the OrgAdmin edit any of them
 * before continuing the wizard.
 *
 * Wizard Next gate: enables when `roles.length > 0`. Mirrors Flutter
 * `:179-183` — backend pre-seeds standard roles on org creation, so the
 * list is always non-empty for a real org; the gate is defensive.
 *
 * Editing is delegated to the existing `<RoleEditDialog>` from
 * `src/settings/components/role-edit-dialog.tsx` (PROD-759). Same dialog
 * the settings page uses — full Flutter parity already shipped including
 * the administrator-lock semantics (tenantId === 1 + 5 protected keys),
 * the Add/Edit/View/Delete column order, and the live PUT save.
 *
 * Permission posture: gated on `Manage_Roles_Permission` with `edit: true`,
 * exactly as the settings Roles page gates this same dialog
 * (`settings/pages/roles-page.tsx`). This previously passed
 * `canEdit={true}` unconditionally on the reasoning that role "1" gates the
 * wizard's mount and an OrgAdmin therefore always holds the permission — but
 * role and permission are separable on this identity (the settings page
 * treats them so for the identical dialog), so the wizard was the one
 * editable surface in the app that skipped the check. Being reachable only by
 * a not-yet-onboarded OrgAdmin narrows the exposure; it does not remove it.
 *
 * Read-only rather than hidden, deliberately: the wizard's Next gate needs
 * `roles.length > 0`, so hiding the list behind a view-gate would strand a
 * user on a step they cannot advance past. Note this is stricter than Flutter,
 * which renders the popup editable and refuses at save with
 * "You are not authorized to update data" (`review_roles_widget.dart:152-168`)
 * — React's disabled-fields posture matches its own sibling page and the
 * PermissionGate convention of hiding rather than failing late.
 */
export interface RolesStepProps {
  /** Called when at least one role exists — gates the wizard's Next button. */
  enableNext: () => void;
}

export function RolesStep({ enableNext }: RolesStepProps) {
  const orgId = useAuthStore((s) => s.user?.organizationId);
  const { data: roles = [], isLoading } = useRolesList(orgId);
  // Same key + check the settings Roles page uses for this same dialog
  // (settings/pages/roles-page.tsx).
  const canEdit = usePermission(['Manage_Roles_Permission'], { edit: true });

  const [editingRole, setEditingRole] = useState<RolesListItem | null>(null);

  // Flutter parity: Next button enables once at least one role exists.
  // List refetches on Update success (RQ invalidation in `useUpdateRole`).
  useEffect(() => {
    if (roles.length > 0) {
      enableNext();
    }
  }, [roles.length, enableNext]);

  return (
    <div className="mx-auto max-w-[720px] px-8 py-10">
      <h1 className="[font:var(--type-card-title)] text-[var(--ink-body)]">
        Review Roles Of Your Practice
      </h1>
      <p className="mt-4 [font:var(--type-body)] text-[var(--ink-meta)]">
        We've selected the most popular types of roles used in a medical practice.
        <br />
        You can keep them as they are or edit them.
      </p>

      <div className="mt-10 rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)]">
        {isLoading ? (
          <p className="px-6 py-10 text-center [font:var(--type-body)] text-[var(--ink-meta)]">
            Loading roles…
          </p>
        ) : roles.length === 0 ? (
          <p className="px-6 py-10 text-center [font:var(--type-body)] text-[var(--ink-meta)]">
            No roles configured for this organisation.
          </p>
        ) : (
          <ul>
            {roles.map((role, index) => (
              <li
                key={`${role.tenantId ?? 'tenant'}-${role.roleId ?? index}`}
                className={
                  index < roles.length - 1
                    ? 'border-b border-[var(--surface-line)]'
                    : ''
                }
              >
                <div className="flex items-center justify-between gap-6 px-6 py-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-[length:var(--type-card-title-size)] leading-[var(--type-card-title-line)] text-[var(--ink-body)]">
                      {role.roleName ?? '—'}
                    </div>
                    {role.roleDescription && (
                      <div className="mt-2 [font:var(--type-body)] text-[var(--ink-meta)]">
                        {role.roleDescription}
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingRole(role)}
                      className="gap-1"
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                      Edit
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <RoleEditDialog
        role={editingRole}
        open={editingRole !== null}
        onOpenChange={(open) => {
          if (!open) setEditingRole(null);
        }}
        canEdit={canEdit}
      />
    </div>
  );
}
